import React, { useState, useEffect, useRef } from 'react';

import {
  matchRoutes,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { SESSION_EXPIRED_ERROR } from '@/assets/AppConstants';

import { useAppContext } from '@/contexts/app';
import { useUserContext } from '@/contexts/user';
import { useAbilityContext } from '@/contexts/ability';
import { useWorkspaceContext } from '@/contexts/workspace';
import { useKeycloakContext } from '@/contexts/keycloak/context';

import { getHomeFromRole } from '@/utils/routes';
import { APP_ROUTES } from '@/assets/MenuItems';

import Forbidden from '@/views/base/Forbidden';
import ErrorBoundary from '@/views/base/ErrorBoundary';

import { LoadingLayout } from '../Loading';
import { LoadingPageNew } from '@/features/login/LoadingPageNew';
import { loginConfig } from '@/features/login/loginConfig';

const LOADING_EXIT_MS = 500;

export const AuthenticatedLayout = () => {
  const { workspace = 0 } = useParams();

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const ability = useAbilityContext();
  const { requestStatus } = useAppContext();
  const { cleanKeycloak } = useKeycloakContext();
  const { userIsLogged } = useUserContext();
  const { workspace: { getWorkspaceRole } } = useWorkspaceContext();

  const hasRole = !!getWorkspaceRole();
  const [loadingLeaving, setLoadingLeaving] = useState(false);
  const [loadingMounted, setLoadingMounted] = useState(!hasRole);
  const prevHasRole = useRef(hasRole);

  const exitTimerRef = useRef(null);

  useEffect(() => {
    if (hasRole && !prevHasRole.current && loginConfig.useNewLoadingPage) {
      setLoadingLeaving(true);
      exitTimerRef.current = setTimeout(() => setLoadingMounted(false), LOADING_EXIT_MS);
    }
    prevHasRole.current = hasRole;
    return () => clearTimeout(exitTimerRef.current);
  }, [hasRole]);

  const finalLocation = location.pathname?.includes('logout')
    ? ''
    : location.pathname;

  if (!userIsLogged()) {
    const redirectTo = `/login?callbackUrl=${encodeURIComponent(
      finalLocation,
    )}`;
    return <Navigate to={redirectTo} replace />;
  }

  const isSessionExpired = requestStatus === SESSION_EXPIRED_ERROR;
  if (isSessionExpired) {
    cleanKeycloak();
    const redirectTo = `/login?callbackUrl=${encodeURIComponent(
      finalLocation,
    )}`;
    return <Navigate to={redirectTo} replace />;
  }

  if (!hasRole) {
    if (!loginConfig.useNewLoadingPage) return <LoadingLayout />;

    return <LoadingPageNew variant="light" />;
  }

  const menuMatchRoutes = matchRoutes(APP_ROUTES, location);

  if (!menuMatchRoutes) {
    const callbackUrl = searchParams.get('callbackUrl')
      || `/${workspace}${getHomeFromRole(getWorkspaceRole())}`
      || '/';

    return (
      <Navigate
        to={callbackUrl}
        replace
      />
    );
  }

  const { length, [length - 1]: routeItem } = menuMatchRoutes;
  const {
    params: { taskId: paramsTaskId },
    route: { key },
  } = routeItem;

  const keyId = paramsTaskId || key;
  const canView = ability.can('view', keyId);
  const isEnabled = ability.can('enabled', keyId);

  return (
    <>
      {loadingMounted && loginConfig.useNewLoadingPage && (
        <LoadingPageNew leaving={loadingLeaving} variant="light" />
      )}
      {canView && isEnabled && (
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      )}
      {
        (!canView || !isEnabled) && (
          <Forbidden />
        )
      }
    </>
  );
};
